/**
 * Pure-function composer for the final notification body string.
 *
 * Web counterpart of the iOS NSE polish step that decides what the rendered
 * notification body should look like AFTER tombstone-collapse and AFTER
 * edit-suffix application. The three threat invariants this module enforces:
 *
 *   1. **Tombstone collapse takes precedence.** If `isTombstoned` is true, the
 *      composer returns the literal string `"[deleted]"`. The cleartext body
 *      passed in is NEVER incorporated into the output, NEVER logged, NEVER
 *      branched on. This holds even when the tombstone arrived out-of-order
 *      (before the body) — the caller pre-checks the tombstone store and
 *      passes `isTombstoned = true`; this composer does not need the
 *      tombstone object itself.
 *
 *   2. **Edits show `(edited)` once and only once.** When `isEdited` is true
 *      AND the message is not tombstoned, the composer appends `" (edited)"`
 *      to the body — but only if the body does not already end with that
 *      exact suffix. This makes the suffix-append idempotent across re-edits:
 *      a body that already carries `(edited)` from a previous edit does not
 *      get `(edited) (edited)` after a second edit.
 *
 *   3. **No diff leakage.** The composer takes a single body string (the
 *      latest version of the message). There is no parameter for `oldBody` /
 *      `previousBody` / `bodyBefore`. The caller is required to pass only the
 *      latest body. Strikethroughs, "was X now Y" comparisons, and other
 *      diff-shaped renderings have no path through this function.
 *
 * Pure logic: no I/O, no `Date.now()`, no `window` / `document` references.
 * Safe to call from any context including a Service Worker.
 *
 * Mirrors the iOS NSE counterpart `NSEBodyComposer.swift` (when that lands).
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The literal body emitted when a message has been tombstoned. The cleartext
 * is never substituted, never logged, never branched on once this constant is
 * returned. Fixed string by design — a runtime-computed placeholder would
 * give a hostile peer a place to inject content.
 */
export const TOMBSTONED_BODY = "[deleted]" as const;

/**
 * The exact suffix appended to an edited message body. Space-prefixed and
 * parenthesised. Compared by exact-suffix equality so that re-edits do not
 * double the suffix.
 *
 * Why not localise: the suffix lives outside the user's chosen UI locale by
 * design — it is metadata about message state, not user-authored content. A
 * future localisation pass would route through this constant.
 */
export const EDITED_SUFFIX = " (edited)" as const;

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

/**
 * Inputs to {@link composeNotificationBody}.
 *
 * `body` is the latest cleartext body the caller decrypted. There is
 * intentionally no `oldBody` / `previousBody` field — the composer never
 * sees a diff.
 */
export interface BodyComposeInputs {
  /** Latest decrypted body. Empty string is permitted (an edit may erase). */
  readonly body: string;
  /** True when an edit-for-everyone has been applied to this message. */
  readonly isEdited: boolean;
  /** True when a tombstone has been recorded for this `(messageId)`. */
  readonly isTombstoned: boolean;
}

/**
 * Produce the final notification body for the given inputs.
 *
 * Decision precedence:
 *   1. `isTombstoned === true` → `TOMBSTONED_BODY` (cleartext NEVER read).
 *   2. `isEdited === true` AND body does not already end with `EDITED_SUFFIX`
 *      → `body + EDITED_SUFFIX`.
 *   3. `isEdited === true` AND body already ends with `EDITED_SUFFIX`
 *      → `body` unchanged (idempotent suffix).
 *   4. Otherwise → `body` unchanged.
 *
 * Pure function — same inputs always produce the same output.
 */
export function composeNotificationBody(inputs: BodyComposeInputs): string {
  // (1) Tombstone collapse. The cleartext is intentionally ignored.
  if (inputs.isTombstoned) {
    return TOMBSTONED_BODY;
  }

  // (2)/(3) Edit suffix — idempotent.
  if (inputs.isEdited) {
    if (inputs.body.endsWith(EDITED_SUFFIX)) {
      return inputs.body;
    }
    return inputs.body + EDITED_SUFFIX;
  }

  // (4) Normal message.
  return inputs.body;
}
